import * as React from 'react';
import { Dispatch } from 'react-redux';
import { StoreState } from '../../../reducers';
import { MultiAppAuthenticationOptionsWrapper, SubmitButtonWrapper, InputWrapper, ForgotPasswordLink, SwitchCompanyViewBlock } from '../styles';
import UrlUtils from '../../../utils/url';
import { I18n, Translate } from 'react-redux-i18n';
import UserAgentUtils from '../../../utils/userAgent';
import { getAuthMethods, MultiAppAuthenticationInfosActionKeys, signIn, setFormValue, updateCredentials } from '../../../containers/MultiAppAuthentication/actions';
import ApiUtils from '../../../utils/api';
import ValidationUtils from '../../../utils/validation';
import { MultiAppAuthenticationInput } from './Input';
import { MultiAppAuthenticationViewMode, AuthMethod } from '../../../containers/MultiAppAuthentication/constants';
import { MultiAppAuthenticationFormState, AspConnexionStatus, MultiAppAuthenticationCredentials } from '../../../containers/MultiAppAuthentication/reducer';
import { createNotification } from '../../../containers/NotificationBar/actions';
import { AuthenticationForm, AuthenticateButton } from '../../Authentication/styles';
import { Api } from '../../../typings/api';
import IndexedDBManager from '../../../utils/indexedDB/manager';

interface MultiAppAuthenticationFormProps {
	dispatch: Dispatch<StoreState>;
	viewMode: MultiAppAuthenticationViewMode;
	switchViewMode: (viewMode: MultiAppAuthenticationViewMode) => void;
	setLoader: (hasLoader: boolean) => void;
	form: MultiAppAuthenticationFormState;
	aspConnexionStatus: AspConnexionStatus;
	userLocale: string; // do not remove. Prop is used to update locale through rerender
	isLoginView: boolean;
	isPasswordView: boolean;
	isCompanyView: boolean;
	isCreationView: boolean;
	galleryConfig: Api.Config;
	credentials: MultiAppAuthenticationCredentials | undefined;
}

interface MultiAppAuthenticationState {
	shouldRememberCredentials: boolean;
	errorMessage?: string;
}

export enum InputName {
	Login = 'login',
	Phone = 'phone',
	Password = 'password',
	Company = 'company',
}

const PASSWORD_MIN_LENGTH = 6;
const PHONE_MIN_LENGTH = 6;

export class MultiAppAuthenticationForm extends React.Component<MultiAppAuthenticationFormProps, MultiAppAuthenticationState> {
	constructor(props: MultiAppAuthenticationFormProps) {
		super(props);
		this.state = {
			shouldRememberCredentials: true,
		};
		this.onClickForgotPassword = this.onClickForgotPassword.bind(this);
	}

	componentWillUpdate(nextProps: MultiAppAuthenticationFormProps) {
		if (this.props.viewMode !== nextProps.viewMode) {
			this.resetErrorMessage();
		}
	}

	handleChangeFormField = (e: React.FormEvent<HTMLInputElement>, inputName: InputName) => {
		const { credentials, dispatch } = this.props;
		const { shouldDisplayErrorMessage } = this;
		const { value } = e.currentTarget;

		if (shouldDisplayErrorMessage) {
			this.resetErrorMessage();
		}

		if (!value && credentials) {
			dispatch(updateCredentials({ [inputName]: null })).then(() =>
				dispatch(setFormValue(inputName, value))
			);
		} else {
			dispatch(setFormValue(inputName, value));
		}
	}

	createDemoAccount(): void {
		const { dispatch, setLoader, form } = this.props;
		const { login, password, phone } = form;

		if (!login || !password || !phone) {
			return dispatch(createNotification('info.fill_all_input_fields'));
		}

		setLoader(true);
		ApiUtils.createAspDemoAccount(login, password, phone)
			.then(() => {
				setLoader(false);
				this.setState({ errorMessage: undefined });
				dispatch(signIn({ login, password, method: AuthMethod.Webpublication }, this.state.shouldRememberCredentials));
			})
			.catch(reason => {
				setLoader(false);
				this.setState({ errorMessage: reason.message });
			});
	}
	handleSendForm = (e: React.FormEvent<HTMLFormElement>): void => {
		e.preventDefault();

		const { dispatch, switchViewMode, isLoginView, isCompanyView, isPasswordView, isCreationView, form, setLoader } = this.props;
		const { login, password, company } = form;
		const { shouldRememberCredentials } = this.state;

		if (!UserAgentUtils.isOnline()) {
			return dispatch(createNotification('info.offline_login'));
		}

		if (isLoginView) {
			if (!ValidationUtils.isValidEmail(login)) {
				this.setState({ errorMessage: I18n.t('error.invalid_email_address') });
				return;
			}
			switchViewMode(MultiAppAuthenticationViewMode.Password);
		} else if (isCompanyView) {
			dispatch(getAuthMethods(company))
				.then((orgAuthMethods) => {
						if (orgAuthMethods.length === 1) {
							const method = orgAuthMethods[0];

							if (method === AuthMethod.Webpublication) {
								switchViewMode(MultiAppAuthenticationViewMode.Login);
							} else {
								switchViewMode(MultiAppAuthenticationViewMode.OrgLogin);
							}
						} else {
							switchViewMode(MultiAppAuthenticationViewMode.Method);
						}
					}
				)
				.catch(() => dispatch({ type: MultiAppAuthenticationInfosActionKeys.COMPANY_ERROR }));
		} else if (isPasswordView) {
			setLoader(true);
			const credentialsPayload = {
				login,
				password,
				method: AuthMethod.Webpublication,
				...(company ? { company } : {}),
			};
			dispatch(signIn(credentialsPayload, shouldRememberCredentials))
				.then(() => setLoader(false))
				.catch(() => setLoader(false));
		} else if (isCreationView) {
			this.createDemoAccount();
		}
	}

	resetErrorMessage(): void {
		const { dispatch, isPasswordView, isCompanyView } = this.props;

		if (isPasswordView || isCompanyView) {
			dispatch({ type: MultiAppAuthenticationInfosActionKeys.RESET_STATUS });
		}

		this.setState({ errorMessage: undefined });
	}

	toggleRememberMe = (): void => {
		const { shouldRememberCredentials } = this.state;

		if (shouldRememberCredentials) {
			IndexedDBManager.removeMultiAppCredentials().then();
		}
		this.setState({ shouldRememberCredentials: !shouldRememberCredentials });
	}

	onClickForgotPassword(): void {
		const { galleryConfig, form } = this.props;
		const { company } = form;
		const url = UrlUtils.getResetAspPasswordUrl(galleryConfig, company);

		window.open(url, '_blank');
	}

	get shouldDisplayErrorMessage(): boolean {
		const { isPasswordView, isCompanyView, aspConnexionStatus } = this.props;

		if (isPasswordView || isCompanyView) {
			return [AspConnexionStatus.AUTH_ERROR, AspConnexionStatus.NO_APP_ERROR, AspConnexionStatus.NO_COMPANY_ERROR].includes(aspConnexionStatus);
		}
		return !!this.state.errorMessage;
	}

	get loginErrorMessageKey(): string {
		const { isLoginView, isCreationView, form } = this.props;
		const { login } = form;
		const { shouldDisplayErrorMessage } = this;
		const isInvalidEmail = shouldDisplayErrorMessage && (isLoginView || isCreationView) && !ValidationUtils.isValidEmail(login);
		const isExistingEmail = shouldDisplayErrorMessage && isCreationView && this.state.errorMessage === 'DUPLICATE_EMAIL--email';

		if (isInvalidEmail) {
			return I18n.t('error.invalid_email_address');
		} else if (isExistingEmail) {
			return I18n.t('error.duplicate_email');
		}
		return '';
	}

	get passwordErrorMessageKey(): string {
		const { isPasswordView, isCreationView, form } = this.props;
		const { password } = form;
		const { shouldDisplayErrorMessage, connectionErrorMessageKey } = this;
		const isShortPassword = shouldDisplayErrorMessage && (isPasswordView || isCreationView) && password.length < PASSWORD_MIN_LENGTH;
		const isConnectionError = shouldDisplayErrorMessage && connectionErrorMessageKey;

		if (isConnectionError) {
			return connectionErrorMessageKey;
		} else if (isShortPassword) {
			return I18n.t('error.field_must_have_at_least', { charsCount: PASSWORD_MIN_LENGTH });
		}
		return '';
	}

	get phoneErrorMessageKey(): string {
		const { isCreationView, form } = this.props;
		const { phone } = form;
		const { shouldDisplayErrorMessage } = this;
		const isShortPhoneNb = shouldDisplayErrorMessage && isCreationView && phone.length < PHONE_MIN_LENGTH;

		return isShortPhoneNb
			? I18n.t('error.field_must_have_at_least', { charsCount: PHONE_MIN_LENGTH })
			: '';
	}

	get companyErrorMessageKey(): string {
		const { isCompanyView } = this.props;
		const { shouldDisplayErrorMessage } = this;
		return shouldDisplayErrorMessage && isCompanyView ? I18n.t('error.invalid_company_name') : '';
	}

	get connectionErrorMessageKey(): string {
		const { isPasswordView, aspConnexionStatus } = this.props;

		if (isPasswordView) {
			return aspConnexionStatus === AspConnexionStatus.AUTH_ERROR ? 'error.invalid_auth' : 'error.no_application_available';
		}
		return '';
	}

	buttonText() {
		const { isLoginView, isCompanyView, isCreationView, isPasswordView } = this.props;

		if (isCompanyView || isLoginView) {
			return 'next';
		} else if (isPasswordView) {
			return 'connect';
		} else if (isCreationView) {
			return 'try_demo';
		} else {
			return '';
		}
	}

	isSubmitButtonDisabled(): boolean {
		const { isLoginView, isCompanyView, isCreationView, isPasswordView, form } = this.props;
		const { login, password, phone, company } = form;
		const { shouldDisplayErrorMessage } = this;
		const enableLoginView = isLoginView && login ? true : false;
		const enablePasswordView = isPasswordView && password ? true : false;
		const enableCompanyView = isCompanyView && company ? true : false;
		const enableCreationView = isCreationView && phone && login && password ? true : false;

		return shouldDisplayErrorMessage || !(enableCompanyView || enableLoginView || enablePasswordView || enableCreationView);
	}

	render() {
		const { isLoginView, isCompanyView, isCreationView, isPasswordView, switchViewMode, form } = this.props;
		const { login, password, company, phone } = form;
		const { shouldRememberCredentials } = this.state;

		return (
			<AuthenticationForm onSubmit={this.handleSendForm}>
				{(isLoginView || isCreationView) &&
					<MultiAppAuthenticationInput
						value={login}
						key={InputName.Login}
						name={InputName.Login}
						onChange={e => this.handleChangeFormField(e, InputName.Login)}
						errorMessage={this.loginErrorMessageKey}
					/>
				}
				{(isPasswordView || isCreationView) &&
					< MultiAppAuthenticationInput
						value={password}
						key={InputName.Password}
						name={InputName.Password}
						onChange={e => this.handleChangeFormField(e, InputName.Password)}
						errorMessage={this.passwordErrorMessageKey}
						autoFocus={isPasswordView ? true : undefined}
					/>
				}
				{isCompanyView &&
					< MultiAppAuthenticationInput
						value={company}
						key={InputName.Company}
						name={InputName.Company}
						onChange={e => this.handleChangeFormField(e, InputName.Company)}
						errorMessage={this.companyErrorMessageKey}
						autoFocus={true}
					/>
				}
				{isCreationView &&
					< MultiAppAuthenticationInput
						value={phone}
						key={InputName.Phone}
						name={InputName.Phone}
						onChange={e => this.handleChangeFormField(e, InputName.Phone)}
						errorMessage={this.phoneErrorMessageKey}
					/>
				}
				{isLoginView &&
					<SwitchCompanyViewBlock onClick={() => switchViewMode(MultiAppAuthenticationViewMode.Company)}>
						<Translate value={`messages.sign_in_options`} />
					</SwitchCompanyViewBlock>
				}

				{isPasswordView &&
					<MultiAppAuthenticationOptionsWrapper>
						<InputWrapper onClick={this.toggleRememberMe}>
							<input type="checkbox" checked={shouldRememberCredentials} readOnly={true} />
							<div />
							<label><Translate value="messages.remember_me" /></label>
						</InputWrapper>
						<ForgotPasswordLink onClick={this.onClickForgotPassword} href="javascript:void();">
							<Translate value="messages.forgot_password" />
						</ForgotPasswordLink>
					</MultiAppAuthenticationOptionsWrapper>
				}
				<SubmitButtonWrapper isLoginView={isLoginView}>
					<AuthenticateButton type="submit" disabled={this.isSubmitButtonDisabled()} >
						<Translate value={`messages.${this.buttonText()}`} />
					</AuthenticateButton>
				</SubmitButtonWrapper>
			</AuthenticationForm>
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/MultiAppAuthentication/Form/index.tsx